"use client";

import { Link } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AnimalInfoCard } from "@/features/Government Observer/animal-info-card";
import { AnimalMetricsChart } from "@/features/Government Observer/animal-metrics-chart";
import { AnimalGrowthChart } from "@/features/Government Observer/animal-growth-chart";
import { AnimalTable } from "@/features/Government Observer/animal-table";
import { AnimalImages } from "@/features/Government Observer/animal-images";
import { apiClient } from "@/api/api.ts";
import { SuspenseFallback } from "@/lib/Utils.Components.tsx";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
} from "@heroui/dropdown";
import { Button } from "@heroui/button";
import { exportToCSV, exportToExcel } from "../Admin/Admin.api";
import {
  RecipientEvaluationTable,
  ReproductiveProcessTable,
} from "@/features/Animals/Farmer.Animal.Profile.tsx";

export function AnimalDetailDashboard({ animalId }: { animalId: any }) {
  // const [animal, setAnimal] = useState<Animal | null>(null);
  // const [loading, setLoading] = useState(true);
  const { data, isLoading } = useQuery({
    queryKey: ["animal", animalId],
    queryFn: async () => {
      if (!animalId) {
        throw new Error("User ID is not available"); // or
        // return
        // a
        // default
        // value,
        // or...
      }
      const response = await apiClient.get(`/animals/animal/${animalId}`, {
        withCredentials: false,
      });

      return response.data;
    },
  });

  if (isLoading) {
    return <SuspenseFallback />;
  }

  if (!data) {
    return <div>no data</div>;
  }

  return (
    <div
      id={"animalDetailBack"}
      className="w-full overflow-hidden flex flex-col space-y-6 z-10 "
    >
      <div className="w-full flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link to="/guest/dashboard">
            <Button
              size="sm"
              startContent={<ArrowLeft className="h-4 w-4" />}
              variant="light"
            ></Button>
          </Link>
          <h2
            id={"animalDetailTitle"}
            className="text-3xl text-gray-600 font-bold tracking-tight"
          >
            {data.name}
          </h2>
        </div>
        <Dropdown showArrow backdrop="blur">
          <DropdownTrigger>
            <Button
              id={"animalExportDropdown"}
              color={"success"}
              endContent={
                <Download
                  className="text-neutral-600 dark:text-slate-200"
                  size="15"
                />
              }
              // isLoading={isPending}
              size="sm"
              // variant="flat"
            >
              Export
            </Button>
          </DropdownTrigger>
          <DropdownMenu
            aria-label="Dropdown menu with description"
            variant="faded"
          >
            <DropdownSection showDivider title={"Export Types"}>
              <DropdownItem
                // shortcut="⌘E"
                key={"recipient_csv"}
                className={"cursor-pointer"}
                color={"success"}
                description={
                  <span className={" text-gray-400"}>
                    export recipient Data csv
                  </span>
                }
                onClick={() =>
                  exportToCSV(data.recipientEvaluation, "recipient_data.csv")
                }
              >
                <span>
                  Export Animal's Recipient Evaluation Data in CSV format
                </span>
              </DropdownItem>
              <DropdownItem
                // shortcut="⌘E"
                key={"recipient_excel"}
                className={"cursor-pointer"}
                color={"success"}
                description={
                  <span className={"text-gray-400"}>
                    export recipient Data to excel
                  </span>
                }
                onPress={() =>
                  exportToExcel(
                    data.recipientEvaluation,
                    "recipient_evaluation.xlsx",
                  )
                }
              >
                <span>
                  Export Animal's Recipient Evaluation Data in Excel format
                </span>
              </DropdownItem>
              <DropdownItem
                // shortcut="⌘E"
                key={"reproductive_csv"}
                className={"cursor-pointer"}
                color={"success"}
                description={
                  <span className={" text-gray-400"}>
                    Export Animal's Reproductive Process Data in CSV Format
                  </span>
                }
                onPress={() =>
                  exportToCSV(data.reproductiveProcess, "reproductive_data.csv")
                }
              >
                <span>
                  Export Animal's Reproductive Process Data CSV format
                </span>
              </DropdownItem>
              <DropdownItem
                // shortcut="⌘E"
                key={"reproductive_excel"}
                className={"cursor-pointer"}
                color={"success"}
                description={
                  <span className={"text-gray-400"}>
                    export reproductive Data to excel
                  </span>
                }
                onPress={() =>
                  exportToExcel(
                    data.reproductiveProcess,
                    "recipient_evaluation.xlsx",
                  )
                }
              >
                <span>
                  Export Animal's Reproductive Process Data Excel format
                </span>
              </DropdownItem>
            </DropdownSection>
          </DropdownMenu>
        </Dropdown>
      </div>

      <Tabs
        id={"animalTabs"}
        className="space-y-4 w-full"
        defaultValue="overview"
      >
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger id={"statisticsCards"} value="metrics">
            Metrics
          </TabsTrigger>
          <TabsTrigger id={"animalImagesTab"} value="images">
            Images
          </TabsTrigger>
        </TabsList>

        <TabsContent
          id={"animalOverviewInfo"}
          className="space-y-4 w-full overflow-x-hidden"
          value="overview"
        >
          <div
            id={"animalOverviewInfo"}
            className="mx-auto grid gap-4 md:grid-cols-2 lg:grid-cols-4 justify-center items-center"
          >
            <Card className="col-span-2">
              <CardHeader>
                <CardTitle>Animal Information</CardTitle>
                <CardDescription>Key details about {data.name}</CardDescription>
              </CardHeader>
              <CardContent>
                <AnimalInfoCard animal={data} />
              </CardContent>
            </Card>

            <Card id={"GrowthMetrics"} className="col-span-2">
              <CardHeader>
                <CardTitle>Growth Metrics</CardTitle>
                <CardDescription>Weight and growth over time</CardDescription>
              </CardHeader>
              <CardContent className="h-[300px]">
                <AnimalGrowthChart animal={data} />
              </CardContent>
            </Card>
          </div>

          <div
            id={"physicalMeasurements"}
            className="grid gap-4 md:grid-cols-2 lg:grid-cols-3"
          >
            <Card className="col-span-2">
              <CardHeader>
                <CardTitle>Physical Measurements</CardTitle>
                <CardDescription>Key physical attributes</CardDescription>
              </CardHeader>
              <CardContent className="h-[300px]">
                <AnimalMetricsChart animal={data} />
              </CardContent>
            </Card>

            <Card id={"breedingInfoCard"}>
              <CardHeader>
                <CardTitle>Breeding Information</CardTitle>
                <CardDescription>
                  Genealogy and breeding details
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="text-sm font-medium">Dam:</div>
                    <div className="text-sm">{data.damName}</div>
                    <div className="text-sm font-medium">Dam Breed:</div>
                    <div className="text-sm">{data.damBreed}</div>
                    <div className="text-sm font-medium">Sire:</div>
                    <div className="text-sm">{data.sireName}</div>
                    <div className="text-sm font-medium">Sire Breed:</div>
                    <div className="text-sm">{data.sireBreed}</div>
                    <div className="text-sm font-medium">Breeding Type:</div>
                    <div className="text-sm">{data.breedingType}</div>
                    <div className="text-sm font-medium">Racial Comp:</div>
                    <div className="text-sm">{data.racialComposition}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className={"w-full flex flex-col space-y-4"}>
            {data.recipientEvaluation && (
              <div id={"reproductiveProcessTable"}>
                <RecipientEvaluationTable data={data.recipientEvaluation} />
              </div>
            )}
            {/* Reproductive Process Table */}
            {data.reproductiveProcess && (
              <div id={"reproductiveProcessTable"}>
                <ReproductiveProcessTable data={data.reproductiveProcess} />
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent className="space-y-4" value="metrics">
          <Card>
            <CardHeader>
              <CardTitle>Detailed Metrics</CardTitle>
              <CardDescription>
                Complete data table with all metrics
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AnimalTable animal={data} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent
          className="space-y-4 flex flex-col justify-center items-center"
          value="images"
        >
          <Card className={"flex flex-col items-center"}>
            <CardHeader className={"w-fit"}>
              <CardTitle>Animal Images</CardTitle>
              <CardDescription>Photo gallery of {data.name}</CardDescription>
            </CardHeader>
            <CardContent>
              <AnimalImages animal={data} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      {/* {data.recipientEvaluation || */}
      {/*   // (data.reproductiveProcess && <CattleDataTables data={data} />)} */}
    </div>
  );
}
